import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  ArrowUpRight,
  TrendingUp,
  Shield,
  Cpu,
  Network,
  Sparkles,
  FileText,
  Briefcase,
  Globe,
  Lightbulb,
  Scale,
  Landmark,
  Building2,
  Plane,
  FileSpreadsheet,
  Home,
  Banknote,
  ScrollText,
} from 'lucide-react';
import { Button } from './ui/button';

const ServicesOverviewPage = () => {
  const [activePillar, setActivePillar] = useState('ai-insights');
  const navigate = useNavigate();
  const sectionRefs = useRef({});

  // ---- PILLAR 1: Latest AI Insights ---------------------------------------
  const aiInsights = {
    id: 'ai-insights',
    icon: Sparkles,
    eyebrow: 'Pillar 01 — Intelligence',
    title: 'Latest AI Insights',
    headline:
      'Turn frontier AI research into real boardroom decisions — faster.',
    intro:
      'AI is rewriting operating models across every sector. Our insights desk tracks GenAI, agentic AI, and regulatory shifts — then translates them into investment, compliance, and talent decisions your CFO, CEO, and Audit Committee can act on this quarter.',
    stats: [
      { value: '72%', label: 'of Indian enterprises have an active GenAI pilot in FY26' },
      { value: '3.4x', label: 'median productivity uplift in finance functions using copilots' },
      { value: '₹4.1L Cr', label: 'India GenAI market projection by 2030' },
    ],
    offerings: [
      {
        name: 'AI Trends & Quarterly Briefings',
        detail:
          'A curated quarterly read-out of frontier models, India-specific use cases, and what it means for your P&L, workforce, and risk posture.',
      },
      {
        name: 'GenAI Readiness Diagnostic',
        detail:
          'A 10-dimension maturity score across data, governance, talent, and infrastructure — benchmarked against sector peers.',
      },
      {
        name: 'Responsible AI Framework',
        detail:
          'Policies, board charters, and model cards aligned to the DPDPA, RBI AI principles, and emerging India AI Act drafts.',
      },
      {
        name: 'Use-Case Discovery Labs',
        detail:
          'Two-day structured sprints that convert business friction into a prioritized, ROI-ranked GenAI backlog.',
      },
      {
        name: 'Agentic AI Architecture Reviews',
        detail:
          'Independent reviews of autonomous agents covering tool-use safety, audit trails, and human-in-the-loop controls.',
      },
      {
        name: 'AI for Finance & Tax',
        detail:
          'Copilot design for close acceleration, GST reconciliation, transfer pricing documentation, and audit workpapers.',
      },
    ],
    sectors: ['BFSI', 'Manufacturing', 'GCCs', 'Healthcare', 'Public Sector', 'Retail'],
  };

  // ---- PILLAR 2: Strategy & Transactions ----------------------------------
  const strategyTransactions = {
    id: 'strategy-transactions',
    icon: TrendingUp,
    eyebrow: 'Pillar 02 — Growth',
    title: 'Strategy & Transactions',
    headline:
      'Capital, conviction, and clean deals — from thesis to Day-100.',
    intro:
      'Whether you are acquiring a competitor, exiting a non-core business, or raising your next round, our S&T team blends deep financial diligence with an operator’s instinct for value. We advise founders, PE/VC funds, family offices, and mid-market groups across the full transaction lifecycle.',
    stats: [
      { value: '$180M+', label: 'of deal value supported across 40+ engagements' },
      { value: '4 weeks', label: 'average turnaround for a full financial & tax DD' },
      { value: '100%', label: 'close rate on mandates taken to term-sheet' },
    ],
    offerings: [
      {
        name: 'Mergers & Acquisitions Advisory',
        detail:
          'Buy-side and sell-side execution — target screening, valuation, structuring, negotiation, and SPA support.',
      },
      {
        name: 'Capital Strategy & Fundraising',
        detail:
          'Debt, equity, and hybrid capital — from teaser and IM to investor outreach, term sheet, and closing.',
      },
      {
        name: 'Valuations & Financial Modeling',
        detail:
          'DCF, SOTP, and relative valuations for M&A, ESOPs, Ind-AS 113, Rule 11UA, and IBC resolution plans.',
      },
      {
        name: 'Due Diligence (Financial, Tax, Commercial)',
        detail:
          'Red-flag and full-scope diligence surfacing quality-of-earnings, working-capital distortions, and hidden liabilities.',
      },
      {
        name: 'Restructuring & Demergers',
        detail:
          'Scheme design under Sections 230-232, slump sales, fast-track mergers, and holding-company simplification.',
      },
      {
        name: 'Post-Merger Integration',
        detail:
          'Day-1 readiness, synergy tracking, chart-of-accounts harmonization, and integration PMO for 100-day plans.',
      },
    ],
    sectors: ['PE & VC', 'Startups', 'Family Offices', 'Mid-Market Corporates', 'Distressed Assets'],
  };

  // ---- PILLAR 3: Risk Consulting Solutions --------------------------------
  const riskConsulting = {
    id: 'risk-consulting',
    icon: Shield,
    eyebrow: 'Pillar 03 — Resilience',
    title: 'Risk Consulting Solutions',
    headline:
      'See risk clearly. Govern it bravely. Turn controls into confidence.',
    intro:
      'Risk has moved from the back office to the boardroom. We help enterprises build integrated risk operating models — covering enterprise, financial, regulatory, cyber, and third-party risk — so leaders can move fast without losing control.',
    stats: [
      { value: '60%', label: 'reduction in audit cycle time using our control-automation approach' },
      { value: '200+', label: 'control frameworks designed across listed and private companies' },
      { value: '0', label: 'material weaknesses reported by audited clients last fiscal' },
    ],
    offerings: [
      {
        name: 'Enterprise Risk Management',
        detail:
          'ERM frameworks aligned to COSO and ISO 31000 — risk appetite, taxonomy, KRIs, and board-level dashboards.',
      },
      {
        name: 'Internal Audit Transformation',
        detail:
          'Shift from cyclical testing to continuous assurance using analytics, RPA, and risk-sensing tools.',
      },
      {
        name: 'Internal Financial Controls (IFC/ICFR)',
        detail:
          'Design, documentation, and testing of financial controls under Section 143(3)(i) and SOX-equivalent regimes.',
      },
      {
        name: 'Regulatory & Compliance Risk',
        detail:
          'SEBI LODR, RBI Master Directions, FEMA, DPDPA, and sector-specific compliance health-checks and remediation.',
      },
      {
        name: 'Third-Party & Vendor Risk',
        detail:
          'Onboarding diligence, ongoing monitoring, and contract-risk frameworks for critical vendor ecosystems.',
      },
      {
        name: 'Forensic & Fraud Risk',
        detail:
          'Whistleblower investigations, transaction forensics, and proactive fraud-risk assessments.',
      },
    ],
    sectors: ['Listed Companies', 'NBFCs & Banks', 'Insurance', 'Pharma', 'GCCs'],
  };

  // ---- PILLAR 4: Technology Consulting Solutions --------------------------
  const technologyConsulting = {
    id: 'technology-consulting',
    icon: Cpu,
    eyebrow: 'Pillar 04 — Transformation',
    title: 'Technology Consulting Solutions',
    headline:
      'Modernize the stack. Unlock the data. Make technology an earnings lever.',
    intro:
      'We help CFOs, CIOs, and founders execute technology programs that actually ship — from cloud-first ERP transformations and data platforms to cyber resilience and finance automation. Our edge: we speak the language of both the controller and the CTO.',
    stats: [
      { value: '45%', label: 'average reduction in month-end close time after finance automation' },
      { value: '12 wks', label: 'from discovery to MVP on our data-platform engagements' },
      { value: '99.9%', label: 'control-evidence coverage achieved on audit-ready data lakes' },
    ],
    offerings: [
      {
        name: 'Digital & Finance Transformation',
        detail:
          'Target operating models for Finance 4.0, shared services, GBS stand-up, and end-to-end process redesign.',
      },
      {
        name: 'ERP & Platform Advisory',
        detail:
          'Vendor-agnostic selection and implementation oversight for SAP S/4HANA, Oracle Fusion, NetSuite, Zoho, and Tally.',
      },
      {
        name: 'Data, Analytics & BI',
        detail:
          'Modern data stack, CFO dashboards, forecasting models, and AI-ready data governance.',
      },
      {
        name: 'Cybersecurity & Cloud Risk',
        detail:
          'Cloud posture reviews, SOC 2 / ISO 27001 readiness, and CISO-as-a-service for scaling teams.',
      },
      {
        name: 'RPA & Intelligent Automation',
        detail:
          'Finance and tax bots — GST 2B reconciliation, vendor onboarding, and statutory return automation.',
      },
      {
        name: 'Emerging Tech Advisory',
        detail:
          'Blockchain, tokenization, and digital-asset accounting and tax positions for forward-leaning enterprises.',
      },
    ],
    sectors: ['Technology', 'BFSI', 'Retail & D2C', 'Manufacturing', 'GCCs', 'SaaS'],
  };

  // ---- PILLAR 5: Managed Services -----------------------------------------
  const managedServices = {
    id: 'managed-services',
    icon: Network,
    eyebrow: 'Pillar 05 — Operate',
    title: 'Managed Services',
    headline:
      'Outcome-based operations for finance, tax, and compliance — at a predictable cost.',
    intro:
      'Our Managed Services practice runs mission-critical finance, tax, and compliance engines for high-growth companies. You get qualified chartered accountants, proven playbooks, and modern tooling — without the overhead of building it in-house.',
    stats: [
      { value: '35%', label: 'typical reduction in total cost of finance operations' },
      { value: '24/7', label: 'compliance calendar coverage across 40+ statutes' },
      { value: 'SLA-led', label: 'delivery with monthly scorecards and QBRs' },
    ],
    offerings: [
      {
        name: 'Finance & Accounting Managed Services',
        detail:
          'Record-to-report, order-to-cash, and procure-to-pay delivered on your ERP, on your calendar, to your SLAs.',
      },
      {
        name: 'Tax Managed Services',
        detail:
          'GST, TDS, income-tax, and transfer-pricing compliance — filings, notices, litigation support, and optimization.',
      },
      {
        name: 'Payroll & HR Compliance',
        detail:
          'End-to-end payroll, PF, ESI, PT, gratuity, and labor-law compliance for India and select GCC jurisdictions.',
      },
      {
        name: 'Virtual CFO Services',
        detail:
          'Fractional CFO leadership — board packs, investor reporting, cash runway, and fundraising readiness.',
      },
      {
        name: 'Regulatory & Secretarial Compliance',
        detail:
          'Companies Act, FEMA, SEBI, and RBI filings managed through a single tracked compliance calendar.',
      },
      {
        name: 'Risk & Internal Audit as a Service',
        detail:
          'Co-sourced or fully outsourced internal audit with a dedicated partner, analytics, and quarterly reporting.',
      },
    ],
    sectors: ['Startups', 'SMEs', 'Family Businesses', 'MNC Subsidiaries', 'PE Portfolio Cos', 'GCCs'],
  };

  const pillars = [
    aiInsights,
    strategyTransactions,
    riskConsulting,
    technologyConsulting,
    managedServices,
  ];

  // ---- Core CA Services (supporting grid) ---------------------------------
  const coreServices = [
    {
      icon: FileText,
      title: 'Assurance, Audit & ESG',
      detail: 'Statutory, Tax, Internal, Bank, ESG Audit, and IPO Readiness Audit.',
    },
    {
      icon: Scale,
      title: 'Direct & Indirect Tax',
      detail: 'Income Tax, GST, TDS, Transfer Pricing, International Tax.',
    },
    {
      icon: Briefcase,
      title: 'Business & IPO Advisory',
      detail: 'Strategy, FP&A, Virtual CFO, IPO Readiness, and valuations.',
    },
    {
      icon: Globe,
      title: 'Foreign Subsidiary & NRI',
      detail: 'Foreign Subsidiary setup, FEMA, technical collaborations, DTAA.',
    },
    {
      icon: Landmark,
      title: 'Regulatory & Secretarial',
      detail: 'Companies Act, SEBI LODR, RBI, and ROC filings.',
    },
    {
      icon: Lightbulb,
      title: 'Startup & Entrepreneur',
      detail: 'Incorporation, ESOP, fundraising, and founder tax planning.',
    },
  ];

  // Smooth scroll to pillar
  const scrollToPillar = (id) => {
    setActivePillar(id);
    const el = sectionRefs.current[id];
    if (el) {
      const top = el.getBoundingClientRect().top + window.scrollY - 180;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  // Track active pillar on scroll
  useEffect(() => {
    const handleScroll = () => {
      const scrollPos = window.scrollY + 240;
      for (const p of pillars) {
        const el = sectionRefs.current[p.id];
        if (el) {
          const { offsetTop, offsetHeight } = el;
          if (scrollPos >= offsetTop && scrollPos < offsetTop + offsetHeight) {
            setActivePillar(p.id);
            return;
          }
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const goToContact = () => {
    navigate('/');
    setTimeout(() => {
      const el = document.getElementById('cta');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 120);
  };

  return (
    <div className="min-h-screen bg-white" data-testid="services-overview-page">
      {/* ================= HERO ================= */}
      <section className="relative bg-[#0a0f1e] pt-32 pb-24 overflow-hidden">
        {/* Grid + glow backdrop */}
        <div className="absolute inset-0 opacity-[0.12] bg-[linear-gradient(to_right,#ffffff18_1px,transparent_1px),linear-gradient(to_bottom,#ffffff18_1px,transparent_1px)] bg-[size:48px_48px]" />
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-brand-blue/20 blur-[160px] rounded-full pointer-events-none" />
        <div className="absolute bottom-0 left-[10%] w-[400px] h-[400px] bg-brand-yellow/10 blur-[140px] rounded-full pointer-events-none" />

        <div className="relative max-w-[1400px] mx-auto px-[7.6923%]">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
            <div className="lg:col-span-8">
              <div className="inline-flex items-center gap-2 border border-white/20 bg-white/5 backdrop-blur px-4 py-2 mb-8">
                <div className="w-1.5 h-1.5 rounded-full bg-brand-yellow animate-pulse" />
                <span className="font-mono text-[11px] tracking-[0.2em] text-white/80 uppercase">
                  Services / RST &amp; Co. Consulting
                </span>
              </div>

              <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl font-black text-white leading-[1.02] tracking-tight mb-8">
                Consulting, engineered for
                <br />
                <span className="text-brand-yellow">the next cycle of growth.</span>
              </h1>

              <p className="text-lg md:text-xl text-white/70 leading-relaxed max-w-2xl mb-10">
                Five specialized practices. One integrated team of chartered accountants, strategists,
                and technologists — purpose-built for entrepreneurs, CFOs, and boards navigating an AI-first economy.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  onClick={goToContact}
                  className="bg-brand-yellow hover:bg-brand-yellow/90 text-gray-900 font-semibold px-8 py-6 h-auto text-base rounded-none group"
                  data-testid="hero-contact-btn"
                >
                  Talk to a Partner
                  <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={18} />
                </Button>
                <Button
                  onClick={() => scrollToPillar('ai-insights')}
                  variant="outline"
                  className="border-white/30 text-white hover:bg-white hover:text-gray-900 font-medium px-8 py-6 h-auto text-base rounded-none bg-transparent"
                  data-testid="hero-explore-btn"
                >
                  Explore the 5 Pillars
                </Button>
              </div>
            </div>

            <div className="lg:col-span-4">
              <div className="border-l-2 border-brand-yellow pl-6">
                <div className="font-mono text-[11px] tracking-[0.2em] text-brand-yellow uppercase mb-3">
                  At a glance
                </div>
                <div className="space-y-4 text-white/80">
                  <div className="flex justify-between items-baseline border-b border-white/10 pb-3">
                    <span className="text-sm">Practices</span>
                    <span className="font-heading text-2xl font-bold text-white">05</span>
                  </div>
                  <div className="flex justify-between items-baseline border-b border-white/10 pb-3">
                    <span className="text-sm">Sector coverage</span>
                    <span className="font-heading text-2xl font-bold text-white">12+</span>
                  </div>
                  <div className="flex justify-between items-baseline border-b border-white/10 pb-3">
                    <span className="text-sm">Years in practice</span>
                    <span className="font-heading text-2xl font-bold text-white">15+</span>
                  </div>
                  <div className="flex justify-between items-baseline">
                    <span className="text-sm">Delivery cities</span>
                    <span className="font-heading text-2xl font-bold text-white">Bengaluru · Pan-India</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================= STICKY PILLAR NAV ================= */}
      <section className="sticky top-28 z-40 bg-white/90 backdrop-blur-xl border-b border-gray-200">
        <div className="max-w-[1400px] mx-auto px-[7.6923%]">
          <div className="flex items-center gap-1 overflow-x-auto py-4 no-scrollbar">
            {pillars.map((p, idx) => {
              const isActive = activePillar === p.id;
              return (
                <button
                  key={p.id}
                  onClick={() => scrollToPillar(p.id)}
                  className={`flex items-center gap-3 px-5 py-2.5 whitespace-nowrap transition-all duration-300 border ${
                    isActive
                      ? 'bg-gray-900 text-white border-gray-900'
                      : 'bg-white text-gray-700 border-gray-200 hover:border-gray-900'
                  }`}
                  data-testid={`pillar-nav-${p.id}`}
                >
                  <span
                    className={`font-mono text-[10px] tracking-[0.15em] ${
                      isActive ? 'text-brand-yellow' : 'text-gray-400'
                    }`}
                  >
                    0{idx + 1}
                  </span>
                  <span className="text-sm font-medium">{p.title}</span>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* ================= PILLAR SECTIONS ================= */}
      {pillars.map((p, idx) => {
        const Icon = p.icon;
        const isDark = idx % 2 === 1; // alternate theme
        return (
          <section
            key={p.id}
            id={p.id}
            ref={(el) => (sectionRefs.current[p.id] = el)}
            className={`py-24 md:py-32 ${isDark ? 'bg-[#0a0f1e] text-white' : 'bg-white text-gray-900'}`}
            data-testid={`pillar-${p.id}`}
          >
            <div className="max-w-[1400px] mx-auto px-[7.6923%]">
              {/* Header */}
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-16">
                <div className="lg:col-span-7">
                  <div
                    className={`inline-block font-mono text-[11px] tracking-[0.25em] uppercase mb-6 ${
                      isDark ? 'text-brand-yellow' : 'text-brand-blue'
                    }`}
                  >
                    {p.eyebrow}
                  </div>
                  <div className="flex items-start gap-5 mb-6">
                    <div
                      className={`w-14 h-14 flex items-center justify-center flex-shrink-0 ${
                        isDark ? 'bg-white/10' : 'bg-gray-900'
                      }`}
                    >
                      <Icon className={isDark ? 'text-brand-yellow' : 'text-white'} size={28} strokeWidth={1.5} />
                    </div>
                    <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-black leading-[1.02] tracking-tight">
                      {p.title}
                    </h2>
                  </div>
                  <p
                    className={`text-xl md:text-2xl font-semibold leading-snug mb-6 ${
                      isDark ? 'text-white' : 'text-gray-900'
                    }`}
                  >
                    {p.headline}
                  </p>
                  <p className={`text-base md:text-lg leading-relaxed max-w-2xl ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
                    {p.intro}
                  </p>
                </div>

                {/* Stats panel */}
                <div className="lg:col-span-5">
                  <div
                    className={`grid grid-cols-1 divide-y ${
                      isDark ? 'divide-white/10 border border-white/10' : 'divide-gray-200 border border-gray-200'
                    }`}
                  >
                    {p.stats.map((s, i) => (
                      <div key={i} className="p-6">
                        <div
                          className={`font-heading text-4xl md:text-5xl font-black mb-2 ${
                            isDark ? 'text-brand-yellow' : 'text-brand-blue'
                          }`}
                        >
                          {s.value}
                        </div>
                        <div className={`text-sm leading-snug ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
                          {s.label}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Offerings grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-transparent mb-12">
                {p.offerings.map((o, i) => (
                  <div
                    key={i}
                    className={`group p-8 transition-all duration-300 border ${
                      isDark
                        ? 'bg-white/[0.02] border-white/10 hover:bg-white/[0.06] hover:border-brand-yellow/50'
                        : 'bg-white border-gray-200 hover:border-gray-900 hover:shadow-[8px_8px_0_0_#0a0f1e]'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-5">
                      <div className={`font-mono text-[11px] tracking-[0.15em] ${isDark ? 'text-white/40' : 'text-gray-400'}`}>
                        0{i + 1}
                      </div>
                      <ArrowUpRight
                        className={`transition-all duration-300 group-hover:rotate-45 ${
                          isDark ? 'text-brand-yellow' : 'text-brand-blue'
                        }`}
                        size={20}
                        strokeWidth={1.5}
                      />
                    </div>
                    <h3
                      className={`font-heading text-xl font-bold mb-3 leading-tight ${
                        isDark ? 'text-white' : 'text-gray-900'
                      }`}
                    >
                      {o.name}
                    </h3>
                    <p className={`text-sm leading-relaxed ${isDark ? 'text-white/60' : 'text-gray-600'}`}>
                      {o.detail}
                    </p>
                  </div>
                ))}
              </div>

              {/* Sectors + CTA row */}
              <div
                className={`flex flex-col md:flex-row md:items-center md:justify-between gap-6 pt-8 border-t ${
                  isDark ? 'border-white/10' : 'border-gray-200'
                }`}
              >
                <div>
                  <div
                    className={`font-mono text-[11px] tracking-[0.2em] uppercase mb-3 ${
                      isDark ? 'text-white/40' : 'text-gray-400'
                    }`}
                  >
                    Industries served
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {p.sectors.map((s, i) => (
                      <span
                        key={i}
                        className={`text-xs px-3 py-1.5 ${
                          isDark ? 'bg-white/10 text-white/80' : 'bg-gray-100 text-gray-700'
                        }`}
                      >
                        {s}
                      </span>
                    ))}
                  </div>
                </div>
                <Button
                  onClick={goToContact}
                  className={`rounded-none px-6 py-5 h-auto group ${
                    isDark
                      ? 'bg-brand-yellow hover:bg-brand-yellow/90 text-gray-900'
                      : 'bg-gray-900 hover:bg-gray-800 text-white'
                  }`}
                  data-testid={`pillar-cta-${p.id}`}
                >
                  Discuss {p.title}
                  <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={18} />
                </Button>
              </div>
            </div>
          </section>
        );
      })}

      {/* ================= CORE CA SERVICES ================= */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-[1400px] mx-auto px-[7.6923%]">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-12">
            <div className="lg:col-span-5">
              <div className="font-mono text-[11px] tracking-[0.25em] text-brand-blue uppercase mb-4">
                Foundation Practice
              </div>
              <h2 className="font-heading text-4xl md:text-5xl font-black text-gray-900 tracking-tight leading-[1.05]">
                The chartered accountant services that built us.
              </h2>
            </div>
            <div className="lg:col-span-7 flex items-end">
              <p className="text-lg text-gray-600 leading-relaxed">
                Alongside our new-age consulting practices, we continue to deliver the deep, detail-oriented
                professional services that our clients have trusted RST &amp; Co. for since day one.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-gray-200 border border-gray-200">
            {coreServices.map((s, i) => {
              const Icon = s.icon;
              return (
                <div
                  key={i}
                  className="bg-white p-8 hover:bg-gray-900 hover:text-white transition-all duration-300 group cursor-pointer"
                  onClick={goToContact}
                >
                  <Icon className="text-brand-blue group-hover:text-brand-yellow mb-5 transition-colors" size={28} strokeWidth={1.5} />
                  <h3 className="font-heading text-xl font-bold mb-2">{s.title}</h3>
                  <p className="text-sm text-gray-600 group-hover:text-white/70 leading-relaxed">
                    {s.detail}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>


      {/* ================= FOREIGN SUBSIDIARY & GLOBAL INDIA SPOTLIGHT ================= */}
      <section
        className="py-24 md:py-32 bg-white relative overflow-hidden"
        data-testid="foreign-subsidiary-spotlight"
      >
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-brand-blue/5 blur-[140px] rounded-full pointer-events-none" />
        <div className="relative max-w-[1400px] mx-auto px-[7.6923%]">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-14">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 border border-brand-blue/30 bg-brand-blue/10 px-3 py-1.5 mb-6">
                <Globe size={14} className="text-brand-blue" />
                <span className="font-mono text-[10px] tracking-[0.25em] text-brand-blue uppercase">
                  Foreign Subsidiary &amp; Global India Spotlight
                </span>
              </div>
              <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-black leading-[1.02] tracking-tight text-gray-900 mb-6">
                Building &amp; running{' '}
                <span className="text-brand-blue">Foreign Subsidiaries</span>
                <br />
                in India — end-to-end.
              </h2>
              <p className="text-lg md:text-xl text-gray-600 leading-relaxed max-w-2xl">
                A dedicated MNC desk for foreign companies establishing or scaling their Indian
                subsidiary — covering entity formation, FEMA / FDI reporting, technical
                collaboration coordination, transfer pricing, ESG and IPO readiness. One partner,
                one plan, one accountable team.
              </p>
            </div>

            <div className="lg:col-span-5 lg:pl-6 lg:border-l border-gray-200">
              <div className="font-mono text-[10px] tracking-[0.25em] text-brand-blue uppercase mb-3">
                Who we serve
              </div>
              <div className="flex flex-wrap gap-2 mb-8">
                {[
                  'MNCs entering India',
                  'GCC / GIC set-ups',
                  'Foreign parent companies',
                  'Indian subs of listed MNCs',
                  'PE-backed cross-border groups',
                  'Tech collaborations & JVs',
                ].map((s, i) => (
                  <span
                    key={i}
                    className="text-xs px-3 py-1.5 bg-gray-100 border border-gray-200 text-gray-800"
                  >
                    {s}
                  </span>
                ))}
              </div>
              <div className="pt-6 border-t border-gray-200 grid grid-cols-3 gap-4">
                <div>
                  <div className="font-heading text-3xl font-black text-brand-blue">15+</div>
                  <div className="text-xs text-gray-600 leading-snug mt-1">Years serving foreign subsidiaries in India</div>
                </div>
                <div>
                  <div className="font-heading text-3xl font-black text-brand-blue">40+</div>
                  <div className="text-xs text-gray-600 leading-snug mt-1">Compliances tracked per subsidiary annually</div>
                </div>
                <div>
                  <div className="font-heading text-3xl font-black text-brand-blue">100%</div>
                  <div className="text-xs text-gray-600 leading-snug mt-1">Partner-led delivery on every mandate</div>
                </div>
              </div>
            </div>
          </div>

          {/* Foreign Subsidiary services grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-gray-200 border border-gray-200">
            {[
              {
                icon: Building2,
                title: 'Foreign Subsidiary Formation',
                detail:
                  'Wholly-owned subsidiary, JV, LLP or branch/liaison office set-up under FEMA — MOA/AOA, PAN/TAN, GST, RBI/AD-bank onboarding, and first-year statutory kit.',
              },
              {
                icon: Landmark,
                title: 'Comprehensive Compliance Management',
                detail:
                  'A single, tracked calendar for Companies Act, FEMA/FDI, Income Tax, GST, Labour, SEBI/RBI and ROC filings — with monthly scorecards for the parent CFO.',
              },
              {
                icon: Cpu,
                title: 'Technical Collaboration Coordination',
                detail:
                  'End-to-end handling of Technology Transfer, Royalty, Licensing and Trademark Use agreements — including RBI/FEMA payment approvals, Form ODI / FC-GPR / FC-TRS filings.',
              },
              {
                icon: TrendingUp,
                title: 'Transfer Pricing & International Tax',
                detail:
                  'TP study, Form 3CEB, Master File, CbCR, APA support, PE risk reviews, DTAA-optimized inter-company arrangements, and secondment tax structuring.',
              },
              {
                icon: Shield,
                title: 'Foreign-Parent Reporting & Audit',
                detail:
                  'US GAAP / IFRS reporting packages, group audit coordination with Big-Four teams, SOX / ICFR testing, and year-end schedules for consolidation.',
              },
              {
                icon: Network,
                title: 'India-Entry Advisory (Set-up to Scale)',
                detail:
                  'Entity structuring, capital & funding routes, ESOP for Indian talent, secretarial & IPR set-up, and exit / winding-down assistance where required.',
              },
            ].map((item, idx) => {
              const Icon = item.icon;
              return (
                <div
                  key={idx}
                  className="bg-white hover:bg-gray-50 p-7 transition-all duration-300 group relative"
                  data-testid={`fs-card-${idx}`}
                >
                  <div className="flex items-center justify-between mb-5">
                    <div className="w-12 h-12 flex items-center justify-center bg-brand-blue/10 group-hover:bg-brand-blue transition-colors">
                      <Icon
                        className="text-brand-blue group-hover:text-white transition-colors"
                        size={22}
                        strokeWidth={1.75}
                      />
                    </div>
                    <span className="font-mono text-[10px] tracking-[0.2em] text-gray-400 group-hover:text-brand-blue transition-colors">
                      0{idx + 1}
                    </span>
                  </div>
                  <h3 className="font-heading text-lg font-bold text-gray-900 mb-3 leading-tight">
                    {item.title}
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{item.detail}</p>
                  <ArrowUpRight
                    size={16}
                    className="absolute top-5 right-5 text-brand-blue opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all duration-300"
                  />
                </div>
              );
            })}
          </div>

          <div className="mt-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <p className="text-sm text-gray-600 max-w-xl">
              Setting up an Indian subsidiary, entering a technical collaboration, or preparing your
              India entity for group consolidation? Get a partner-led response within one business
              day.
            </p>
            <Button
              onClick={goToContact}
              className="bg-gray-900 hover:bg-gray-800 text-white font-semibold px-7 py-5 h-auto rounded-none group"
              data-testid="fs-cta-btn"
            >
              Talk to our MNC desk
              <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={18} />
            </Button>
          </div>
        </div>
      </section>

      {/* ================= NRI SPOTLIGHT ================= */}
      <section
        className="py-24 md:py-32 bg-gradient-to-br from-[#0a0f1e] via-[#0f1d3a] to-[#0a0f1e] text-white relative overflow-hidden"
        data-testid="nri-spotlight"
      >
        <div className="absolute inset-0 opacity-[0.08] bg-[linear-gradient(to_right,#ffffff22_1px,transparent_1px),linear-gradient(to_bottom,#ffffff22_1px,transparent_1px)] bg-[size:48px_48px] pointer-events-none" />
        <div className="absolute top-10 right-[-10%] w-[600px] h-[600px] bg-brand-yellow/10 blur-[160px] rounded-full pointer-events-none" />

        <div className="relative max-w-[1400px] mx-auto px-[7.6923%]">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-14">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 border border-brand-yellow/40 bg-brand-yellow/10 px-3 py-1.5 mb-6">
                <Plane size={14} className="text-brand-yellow" />
                <span className="font-mono text-[10px] tracking-[0.25em] text-brand-yellow uppercase">
                  NRI &amp; Cross-Border Spotlight
                </span>
              </div>
              <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-black leading-[1.02] tracking-tight mb-6">
                Built for Indians abroad —
                <br />
                <span className="text-brand-yellow">visas, property, and repatriation, simplified.</span>
              </h2>
              <p className="text-lg md:text-xl text-white/70 leading-relaxed max-w-2xl">
                A dedicated NRI desk handles the moving parts of cross-border life — from
                consulate-grade documentation to FEMA-compliant fund movement — so you can focus
                on living, working, and investing globally.
              </p>
            </div>

            <div className="lg:col-span-5 lg:pl-6 lg:border-l border-white/10">
              <div className="font-mono text-[10px] tracking-[0.25em] text-brand-yellow uppercase mb-3">
                Who we serve
              </div>
              <div className="flex flex-wrap gap-2">
                {['NRIs', 'PIOs', 'OCIs', 'Foreign Nationals', 'Returning Indians', 'Global Indian Families'].map(
                  (s, i) => (
                    <span
                      key={i}
                      className="text-xs px-3 py-1.5 bg-white/10 border border-white/10 text-white/85"
                    >
                      {s}
                    </span>
                  )
                )}
              </div>
              <div className="mt-8 pt-6 border-t border-white/10">
                <div className="font-mono text-[10px] tracking-[0.25em] text-brand-yellow uppercase mb-2">
                  Turnaround
                </div>
                <p className="text-sm text-white/70 leading-relaxed">
                  Most Visa-grade Net Worth Certificates and Financial Statements
                  delivered within <span className="text-white font-semibold">5 working days</span>.
                </p>
              </div>
            </div>
          </div>

          {/* NRI services grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
            {[
              {
                icon: ScrollText,
                title: 'Net Worth Certificates for Visa',
                detail:
                  'CA-certified Net Worth statements accepted by consulates and embassies for student, dependent, investor, and migration visa applications.',
              },
              {
                icon: FileSpreadsheet,
                title: 'Financial Statements for Visa Applications',
                detail:
                  'Visa-ready balance sheets, income statements, and bank reconciliations prepared and signed by a chartered accountant.',
              },
              {
                icon: Globe,
                title: 'DTAA & Foreign Tax Credit Consulting',
                detail:
                  'Treaty analysis, residency determination, Form 67/10F support, and FTC claims to eliminate double taxation across jurisdictions.',
              },
              {
                icon: Home,
                title: 'Property Transaction Consulting',
                detail:
                  'End-to-end advisory on NRI property purchase / sale — TDS u/s 195, capital-gains optimization, Form 13 lower-deduction certificates.',
              },
              {
                icon: Banknote,
                title: 'Repatriation Consulting & Compliance',
                detail:
                  'NRO → NRE → overseas remittances, Form 15CA/15CB, USD 1M scheme, RBI/AD bank documentation, and end-to-end repatriation execution.',
              },
              {
                icon: Plane,
                title: 'Returning NRI & Estate Planning',
                detail:
                  'RNOR planning, FEMA reclassification, will & succession structuring, and cross-border estate-tax considerations.',
              },
            ].map((item, idx) => {
              const Icon = item.icon;
              return (
                <div
                  key={idx}
                  className="bg-[#0a0f1e] hover:bg-white/[0.04] p-7 transition-all duration-300 group relative"
                  data-testid={`nri-card-${idx}`}
                >
                  <div className="flex items-center justify-between mb-5">
                    <div className="w-12 h-12 flex items-center justify-center bg-brand-yellow/10 group-hover:bg-brand-yellow/20 transition-colors">
                      <Icon className="text-brand-yellow" size={22} strokeWidth={1.75} />
                    </div>
                    <span className="font-mono text-[10px] tracking-[0.2em] text-white/30 group-hover:text-brand-yellow transition-colors">
                      0{idx + 1}
                    </span>
                  </div>
                  <h3 className="font-heading text-lg font-bold text-white mb-3 leading-tight">
                    {item.title}
                  </h3>
                  <p className="text-sm text-white/65 leading-relaxed">{item.detail}</p>
                  <ArrowUpRight
                    size={16}
                    className="absolute top-5 right-5 text-brand-yellow opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all duration-300"
                  />
                </div>
              );
            })}
          </div>

          <div className="mt-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <p className="text-sm text-white/60 max-w-xl">
              Need an urgent Visa-grade certificate or property TDS support? Tell us your timeline
              and we will respond within one business day.
            </p>
            <Button
              onClick={goToContact}
              className="bg-brand-yellow hover:bg-brand-yellow/90 text-gray-900 font-semibold px-7 py-5 h-auto rounded-none group"
              data-testid="nri-cta-btn"
            >
              Talk to our NRI desk
              <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={18} />
            </Button>
          </div>
        </div>
      </section>


      {/* ================= CLOSING CTA ================= */}
      <section className="py-24 bg-[#0a0f1e] relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.08] bg-[linear-gradient(to_right,#ffffff22_1px,transparent_1px),linear-gradient(to_bottom,#ffffff22_1px,transparent_1px)] bg-[size:48px_48px]" />
        <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-brand-yellow/10 blur-[160px] rounded-full" />
        <div className="relative max-w-[1400px] mx-auto px-[7.6923%]">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-8">
              <div className="font-mono text-[11px] tracking-[0.25em] text-brand-yellow uppercase mb-6">
                Start the conversation
              </div>
              <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-black text-white tracking-tight leading-[1.02] mb-6">
                Bring us a hard problem.
                <br />
                We’ll bring the partners.
              </h2>
              <p className="text-lg text-white/70 max-w-2xl leading-relaxed">
                Every engagement at RST &amp; Co. is led by a partner — not handed off. Tell us what you are
                building, investing in, or worried about, and we&apos;ll respond within one business day.
              </p>
            </div>
            <div className="lg:col-span-4 flex lg:justify-end">
              <Button
                onClick={goToContact}
                className="bg-brand-yellow hover:bg-brand-yellow/90 text-gray-900 font-semibold px-10 py-7 h-auto text-base rounded-none group"
                data-testid="closing-cta-btn"
              >
                Book a Consultation
                <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicesOverviewPage;
